import { useLazyQuery } from '@apollo/client';
import {
  BaseUser,
  GetBaseUserResponse,
  LoginUserResponse,
  LogoutUserResponse,
} from '@/api/types/user.types';
import { GET_BASE_USER } from '@/api/queries/user.queries';
import { AuthStatus, useAuthStore } from '@/store/useAuthStore';
import { useSafeMutation } from '@/api/hooks/useSafeMutation';
import { LOGIN_USER, LOGOUT_USER } from '@/api/mutations/users.mutations';
import { ROUTES } from '@/constants/routes';

export function useAuth() {
  const [
    setIsAuthLoaded,
    setUserId,
    setAuthStatus,
    setIsAuthenticated,
    setUserInfo,
  ] = useAuthStore(state => [
    state.setIsAuthLoaded,
    state.setUserId,
    state.setAuthStatus,
    state.setIsAuthenticated,
    state.setUserInfo,
  ]);

  const [getBaseUserQuery] = useLazyQuery<GetBaseUserResponse>(GET_BASE_USER, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const getBaseUser = async () => {
    try {
      const { data } = await getBaseUserQuery();
      handleUser(data?.currentUser ?? {});
    } catch (error) {
      handleError();
    }
  };

  const [loginUser] = useSafeMutation<LoginUserResponse>(LOGIN_USER, {
    onCompleted: data => handleUser({ id: data?.loginUser?.entity }),
    onError: handleError,
  });

  const [logoutUser] = useSafeMutation<LogoutUserResponse>(LOGOUT_USER, {
    onCompleted: data => {
      if (data.logoutUser.entity) {
        setUserId(null);
        handleAnonymous({});
        window.location.href = ROUTES.home;
      }
    },
  });

  function handleAnonymous(user: Partial<BaseUser>) {
    setIsAuthenticated(false);
    setUserInfo(user);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleAuthenticated(user: Partial<BaseUser>) {
    setUserId(user.id ?? null);
    setIsAuthenticated(true);
    setUserInfo(user);
    setAuthStatus(AuthStatus.Authenticated);
  }

  function handleError() {
    setIsAuthLoaded(true);
    setUserId(null);
    setUserInfo({});
    setIsAuthenticated(false);
    setAuthStatus(AuthStatus.Unauthenticated);
  }

  function handleUser(user: Partial<BaseUser>) {
    setIsAuthLoaded(true);
    setUserId(null);

    if (user.id) {
      handleAuthenticated(user);
    } else {
      handleAnonymous(user);
    }
  }

  return { getBaseUser, loginUser, logoutUser };
}
